import React, { useCallback } from 'react';
import { FaTrash } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog, openDialog } from '../../../../../app/dialog/dialog.actions';
import {
  deletePaymentMethod,
  setDefaultPaymentMethod,
} from '../../../../../app/page/subscription/subscription.actions';
import { paymentMethodSelectors } from '../../../../../app/resource/payment-method/payment-method.selectors';
import { IPaymentMethod } from '../../../../../app/resource/payment-method/payment-method.types';
import AmexIcon from '../../../../resources/images/amex.svg';
import MastercardIcon from '../../../../resources/images/mastercard.svg';
import VisaIcon from '../../../../resources/images/visa.svg';

const CARD_BRAND_ICONS: Record<string, string> = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
  amex: AmexIcon,
};

const formatBrandLabel = (brand: string | null) => {
  switch ((brand || '').toLowerCase()) {
    case 'visa':
      return 'VISA';
    case 'mastercard':
      return 'Master Card';
    case 'amex':
      return 'American Express';
    default:
      return (brand || 'Card').replace(/\b\w/g, (character) => character.toUpperCase());
  }
};

const getBrandIcon = (brand: string | null) => CARD_BRAND_ICONS[(brand || '').toLowerCase()] ?? null;

const formatExpiry = (expMonth: number | null, expYear: number | null) => {
  if (!expMonth || !expYear) {
    return null;
  }

  const month = String(expMonth).padStart(2, '0');
  return `${month}/${expYear}`;
};

export const PaymentMethods = () => {
  const dispatch = useDispatch();
  const paymentMethods = useSelector(paymentMethodSelectors.selectResourceList) as IPaymentMethod[];

  const sortedMethods = [...paymentMethods].sort((a, b) => {
    if (a.isDefault === b.isDefault) {
      return 0;
    }
    return a.isDefault ? -1 : 1;
  });

  const onAddPaymentMethod = useCallback(() => {
    dispatch(openDialog('addPaymentMethodDialog'));
  }, [dispatch]);

  const onSetDefault = useCallback(
    (paymentMethodId: IPaymentMethod['id']) => {
      dispatch(setDefaultPaymentMethod(paymentMethodId));
    },
    [dispatch],
  );

  const onDelete = useCallback(
    (paymentMethodId: IPaymentMethod['id']) => {
      dispatch(
        openDialog('deleteDialog', {
          title: 'Delete payment method',
          message: 'Are you sure you want to delete this card?',
          onConfirm: () => {
            dispatch(closeDialog('deleteDialog'));
            dispatch(deletePaymentMethod(paymentMethodId));
          },
        }),
      );
    },
    [dispatch],
  );

  return (
    <div className="col px-0">
      <div className="field-container px-2 mb-5">
        <div className="panel theme-gradient-2 py-1 px-2 flex aic mb-3">
          <h2 className="f-14 white bold">PAYMENT METHODS</h2>
        </div>
        <div className="mb-3">
          <div className="f-16 bold mb-1">Credit Cards</div>
          <div className="f-1 blueGray mb-2">
            You have {sortedMethods.length} credit card{sortedMethods.length === 1 ? '' : 's'} saved.
          </div>
          <button type="button" className="btn darkGray-bg white" onClick={onAddPaymentMethod}>
            + Add a Payment Method
          </button>
        </div>
        {sortedMethods.length === 0 ? (
          <div className="f-14 blueGray">No payment methods saved yet.</div>
        ) : (
          <div className="panel">
            {sortedMethods.map((method) => {
              const brandLabel = formatBrandLabel(method.brand);
              const brandIcon = getBrandIcon(method.brand);
              const last4 = method.last4 || '';
              const expiry = formatExpiry(method.expMonth, method.expYear);

              return (
                <div className="subscription-detail-row flex aic jcsb py-2" key={method.id}>
                  <div className="flex aic">
                    <div className="payment-method-card-logo mr-2">
                      {brandIcon ? (
                        <img src={brandIcon} alt="" aria-hidden="true" className="payment-method-card-logo-image" />
                      ) : (
                        <span className="payment-method-card-logo-fallback f-1 bold blueGray">{brandLabel}</span>
                      )}
                      {method.isDefault && (
                        <span className="payment-method-primary-badge">
                          <i className="icon-check white" />
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="f-16 bold">
                        {brandLabel}
                        {last4 ? `, ${last4}` : ''}
                      </div>
                      {expiry && <div className="f-1 blueGray">Expires {expiry}</div>}
                    </div>
                  </div>
                  <div className="flex aic">
                    {method.isDefault ? (
                      <button type="button" className="btn green-bg white mr-1" disabled>
                        <i className="icon-check mr-1" />
                        Primary
                      </button>
                    ) : (
                      <button type="button" className="btn btn-border mr-1" onClick={() => onSetDefault(method.id)}>
                        Make as Primary
                      </button>
                    )}
                    {sortedMethods.length > 1 && (
                      <button
                        type="button"
                        className="p-1 border-radius-100 dark-gray-border pointer"
                        style={{
                          height: 35,
                          width: 35,
                          background: 'none',
                          boxSizing: 'border-box',
                          color: 'inherit',
                          fontSize: 'inherit',
                        }}
                        title="Delete payment method"
                        onClick={() => onDelete(method.id)}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
